import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSearch, faTrash, faTimes, faChevronRight,
  faComments, faSpinner, faClock
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from "../config/api";
import { notifyError, notifyWarning } from "../Home/Error/errors";
import './ConversationHistory.scss';

function ConversationHistory({
  userId,
  isOpen,
  onToggle,
  onLoadConversation,
  onNewConversation,
  currentConversationId
}) {
  const [conversations, setConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const searchTimeoutRef = useRef(null);

  const fetchConversations = useCallback(async (search = "") => {
    if (!userId) return;
    setLoading(true);
    try {
      let url = `${API_BASE}/api/chat/history?userId=${encodeURIComponent(userId)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-APP-TOKEN': 'token',
          'X-ACCOUNT-ID': 'account-id',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      } else {
        notifyError("Historial", "No se pudo cargar el historial de conversaciones.");
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      notifyError("Historial", "No hay conexión con el servidor. Verifica tu conexión.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      setDeleteConfirmId(null);
    }
  }, [isOpen, fetchConversations]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchConversations(value);
    }, 300);
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      const url = `${API_BASE}/api/chat/history/${id}?userId=${encodeURIComponent(userId)}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-APP-TOKEN': 'token',
          'X-ACCOUNT-ID': 'account-id',
        }
      });
      if (response.ok) {
        setConversations(prev => prev.filter(c => c.id !== id));
        setDeleteConfirmId(null);
      } else {
        notifyError("Eliminar", "No se pudo eliminar la conversación.");
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      notifyError("Eliminar", "Error de conexión al eliminar la conversación.");
    }
  };

  const handleLoad = async (conv) => {
    try {
      const url = `${API_BASE}/api/chat/history/${conv.id}?userId=${encodeURIComponent(userId)}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-APP-TOKEN': 'token',
          'X-ACCOUNT-ID': 'account-id',
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.corrupted) {
          notifyWarning("Conversación incompleta", "Algunos mensajes de esta conversación no se pudieron recuperar.");
        }
        onLoadConversation(
          data.conversation.messages,
          data.conversation.id,
          data.conversation.title
        );
        onToggle();
      } else {
        notifyError("Cargar conversación", "No se pudo cargar la conversación seleccionada.");
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      notifyError("Cargar conversación", "Error de conexión al cargar la conversación.");
    }
  };

  const groupByDate = (convs) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = {
      'Hoy': [],
      'Ayer': [],
      'Esta semana': [],
      'Anteriores': []
    };

    convs.forEach(conv => {
      const date = new Date(conv.updatedAt);
      if (date >= today) groups['Hoy'].push(conv);
      else if (date >= yesterday) groups['Ayer'].push(conv);
      else if (date >= weekAgo) groups['Esta semana'].push(conv);
      else groups['Anteriores'].push(conv);
    });

    return groups;
  };

  const grouped = groupByDate(conversations);

  return (
    <div className={`floating-menu conversation-history-panel ${isOpen ? 'open' : ''}`}>
      <button className="toggle-history" onClick={onToggle} title="Historial">
        <FontAwesomeIcon icon={isOpen ? faChevronRight : faClock} />
      </button>

      <div className="chat-history">
        <div className="history-header">
          <h3>
            <FontAwesomeIcon icon={faComments} /> Historial
          </h3>
          <button
            className="new-conversation-btn"
            onClick={() => { onNewConversation(); onToggle(); }}
          >
            <FontAwesomeIcon icon={faPlus} /> Nueva
          </button>
        </div>

        <div className="history-search">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar conversaciones..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => handleSearch("")}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        <div className="history-list">
          {loading ? (
            <div className="history-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Cargando...</span>
            </div>
          ) : conversations.length === 0 ? (
            <div className="history-empty">
              <FontAwesomeIcon icon={faClock} />
              <p>No hay conversaciones guardadas</p>
            </div>
          ) : (
            Object.entries(grouped).map(([label, convs]) => {
              if (convs.length === 0) return null;
              return (
                <div key={label} className="history-group">
                  <div className="group-label">{label}</div>
                  {convs.map(conv => (
                    <div
                      key={conv.id}
                      className={`history-item ${conv.id === currentConversationId ? 'active' : ''}`}
                    >
                      <div className="item-content" onClick={() => handleLoad(conv)}>
                        <div className="item-title">{conv.title}</div>
                        <div className="item-meta">
                          <span className="item-time">
                            {new Date(conv.updatedAt).toLocaleTimeString('es-MX', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {conv.businessLine && conv.businessLine !== 'Todos' && (
                            <span className="item-badge">{conv.businessLine}</span>
                          )}
                        </div>
                      </div>
                      <div className="item-actions">
                        {deleteConfirmId === conv.id ? (
                          <div className="delete-confirm">
                            <span>Eliminar?</span>
                            <button
                              className="confirm-yes"
                              onClick={(e) => handleDelete(conv.id, e)}
                            >
                              Si
                            </button>
                            <button
                              className="confirm-no"
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            className="delete-btn"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(conv.id); }}
                            title="Eliminar"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default ConversationHistory;
